/**
 * Generato automaticamente.
 * OpenAPI
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  GetTipiOnereParams,
  TipoOnereDTO,
  TipoOnereRequest,
  WsProblema,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Restituisce l'onere e il tipo onere al quale è associato
 * @summary Dettaglio Onere
 */
export const getTipiOnereId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoOnereDTO>(
    { url: `/framework-cfg-domande/tipi-onere/${id}`, method: 'get', signal },
    options,
  );
};

export const getGetTipiOnereIdQueryKey = (id: string) =>
  [`/framework-cfg-domande/tipi-onere/${id}`] as const;

export const getGetTipiOnereIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiOnereId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiOnereId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiOnereId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTipiOnereIdQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTipiOnereId>>> = ({
    signal,
  }) => getTipiOnereId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetTipiOnereIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiOnereId>>
>;
export type GetTipiOnereIdQueryError = ErrorType<unknown>;

/**
 * @summary Dettaglio Onere
 */
export const useGetTipiOnereId = <
  TData = Awaited<ReturnType<typeof getTipiOnereId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiOnereId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiOnereIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna un onere.
 * @summary Modifica Onere
 */
export const putTipiOnereId = (
  id: string,
  tipoOnereRequest: TipoOnereRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<TipoOnereDTO>(
    {
      url: `/framework-cfg-domande/tipi-onere/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: tipoOnereRequest,
    },
    options,
  );
};

export const getPutTipiOnereIdMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTipiOnereId>>,
    TError,
    { id: string; data: TipoOnereRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putTipiOnereId>>,
  TError,
  { id: string; data: TipoOnereRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putTipiOnereId>>,
    { id: string; data: TipoOnereRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putTipiOnereId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutTipiOnereIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putTipiOnereId>>
>;
export type PutTipiOnereIdMutationBody = TipoOnereRequest;
export type PutTipiOnereIdMutationError = ErrorType<WsProblema>;

/**
 * @summary Modifica Onere
 */
export const usePutTipiOnereId = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTipiOnereId>>,
    TError,
    { id: string; data: TipoOnereRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutTipiOnereIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Consente la cancellazione di un Onere. L’operazione di cancellazione è consentita solo ed esclusivamente se l’onere non risulta associato a nessuna Tipologia di Domanda.
 * @summary Elimina un onere
 */
export const deleteTipiOnereId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    { url: `/framework-cfg-domande/tipi-onere/${id}`, method: 'delete' },
    options,
  );
};

export const getDeleteTipiOnereIdMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTipiOnereId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteTipiOnereId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteTipiOnereId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteTipiOnereId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteTipiOnereIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteTipiOnereId>>
>;

export type DeleteTipiOnereIdMutationError = ErrorType<WsProblema>;

/**
 * @summary Elimina un onere
 */
export const useDeleteTipiOnereId = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTipiOnereId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteTipiOnereIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ottiene l'elenco degli oneri
 * @summary Elenco Oneri
 */
export const getTipiOnere = (
  params?: GetTipiOnereParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoOnereDTO[]>(
    { url: `/framework-cfg-domande/tipi-onere`, method: 'get', params, signal },
    options,
  );
};

export const getGetTipiOnereQueryKey = (params?: GetTipiOnereParams) =>
  [`/framework-cfg-domande/tipi-onere`, ...(params ? [params] : [])] as const;

export const getGetTipiOnereQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiOnere>>,
  TError = ErrorType<unknown>,
>(
  params?: GetTipiOnereParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiOnere>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof getTipiOnere>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTipiOnereQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTipiOnere>>> = ({
    signal,
  }) => getTipiOnere(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetTipiOnereQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiOnere>>
>;
export type GetTipiOnereQueryError = ErrorType<unknown>;

/**
 * @summary Elenco Oneri
 */
export const useGetTipiOnere = <
  TData = Awaited<ReturnType<typeof getTipiOnere>>,
  TError = ErrorType<unknown>,
>(
  params?: GetTipiOnereParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiOnere>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiOnereQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Crea un nuovo onere e ne restituisce l'id.
 * @summary Crea Onere
 */
export const postTipiOnere = (
  tipoOnereRequest: TipoOnereRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cfg-domande/tipi-onere`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: tipoOnereRequest,
    },
    options,
  );
};

export const getPostTipiOnereMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTipiOnere>>,
    TError,
    { data: TipoOnereRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postTipiOnere>>,
  TError,
  { data: TipoOnereRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postTipiOnere>>,
    { data: TipoOnereRequest }
  > = props => {
    const { data } = props ?? {};

    return postTipiOnere(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostTipiOnereMutationResult = NonNullable<
  Awaited<ReturnType<typeof postTipiOnere>>
>;
export type PostTipiOnereMutationBody = TipoOnereRequest;
export type PostTipiOnereMutationError = ErrorType<WsProblema>;

/**
 * @summary Crea Onere
 */
export const usePostTipiOnere = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTipiOnere>>,
    TError,
    { data: TipoOnereRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostTipiOnereMutationOptions(options);

  return useMutation(mutationOptions);
};
